import type { ConnectorItem } from '@ralph/contracts'
import clsx from 'clsx'
import { ChevronRight, Folder, FolderOpen, Minus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button, Spinner } from '@/components/ui/primitives.tsx'
import { useItems } from '@/hooks/use-connectors.ts'
import { useI18n } from '@/i18n/index.ts'
import { filesAreTickable, folderRowDetail } from './connected-folders.ts'

/**
 * What is included, one level at a time (PRD-25 D-16).
 *
 * Not a tree. A tri-state tree over fifty thousand items cannot be scanned, cannot be
 * keyboard-navigated in fewer than several hundred stops, and asks for the deepest decision
 * at the shallowest moment of understanding. This is a breadcrumb and one level: folders
 * first, each with a real checkbox, the post-filter count and the size beneath; files
 * individually tickable only when a level is small enough to read, otherwise one summary line
 * that says how to choose instead.
 *
 * Selection lives in rules keyed by remote id on the server, so drilling down and back keeps
 * every choice; this component only reports "tick this node" / "untick this node" upward. The
 * checkbox contract:
 *   - ticked = everything under here, including what appears later;
 *   - unticked = nothing under here, including what appears later;
 *   - mixed = some, drawn with a dash and the ratio beside it; clicking a mixed box ticks
 *     everything, never unticks.
 * `indeterminate` is a DOM property, not an attribute: it is set through a ref, with
 * `aria-checked="mixed"` beside it, because React will not do it from JSX.
 */

export type PendingRule = { remoteId: string; effect: 'include' | 'exclude' | null }

export const SelectionTable = ({
  folderId,
  rootName,
  pending,
  onToggle,
  readOnly = false,
  refresh = false,
  parent,
  onNavigate
}: {
  folderId: string
  rootName: string
  /** Rules chosen but not yet applied, keyed by remote id; drawn on top of what the server says. */
  pending: ReadonlyMap<string, PendingRule>
  onToggle: (item: ConnectorItem, effect: 'include' | 'exclude' | null) => void
  readOnly?: boolean
  /** Re-read the level while a count is still running. */
  refresh?: boolean
  parent: string | null
  onNavigate: (remoteId: string | null) => void
}) => {
  // In the body, not at module scope: a size — or a word — rendered from a frozen locale is
  // the silent half of PRD-27 F-2/F-3, "1.234 MB" beside "1,234 files" on the same screen and
  // the previous language beside both.
  const fmt = useI18n()
  const { formatBytes } = fmt
  const t = fmt.t.knowledgeFolders
  const [offset, setOffset] = useState(0)
  const page = useItems(folderId, { parent, filter: 'all', query: '', offset }, refresh)
  const [accumulated, setAccumulated] = useState<ConnectorItem[]>([])

  // Pages accumulate; a change of level or a refresh starts over.
  const pageItems = page.data?.items
  useEffect(() => {
    if (!pageItems) return
    setAccumulated((prev) => (offset === 0 ? pageItems : [...prev.slice(0, offset), ...pageItems]))
  }, [pageItems, offset])
  // biome-ignore lint/correctness/useExhaustiveDependencies: the level changed; the accumulated pages belong to the old one
  useEffect(() => {
    setOffset(0)
    setAccumulated([])
  }, [parent])

  const items = accumulated.length > 0 ? accumulated : (page.data?.items ?? [])
  const folders = items.filter((item) => item.kind === 'folder')
  const files = items.filter((item) => item.kind === 'file')
  const total = page.data?.total ?? 0
  const breadcrumb = page.data?.breadcrumb ?? []
  // Files are ticked one by one only once every page of the level has arrived: a first
  // page of folders says nothing about how many files follow it.
  const allLoaded = items.length >= total
  const filesAtLevel = total - folders.length
  const tickable = allLoaded && filesAreTickable(filesAtLevel)

  const effectiveOf = (item: ConnectorItem): { state: 'all' | 'none' | 'some'; rule: 'include' | 'exclude' | null } => {
    const local = pending.get(item.remoteId)
    if (local) {
      return {
        state: local.effect === 'exclude' ? 'none' : local.effect === 'include' ? 'all' : (item.effective ?? 'all'),
        rule: local.effect
      }
    }
    if (item.kind === 'file')
      return {
        state:
          item.decision === 'included' ||
          item.reason === 'unsupported' ||
          item.reason === 'too_large' ||
          item.reason === 'too_old' ||
          item.reason === 'type'
            ? 'all'
            : 'none',
        rule: item.rule
      }
    return { state: item.effective ?? 'all', rule: item.rule }
  }

  return (
    <div>
      <nav aria-label={t.selection.pathLabel} className="flex flex-wrap items-center gap-1 text-[12.5px]">
        <button
          type="button"
          onClick={() => onNavigate(null)}
          className={clsx('rounded px-1 py-0.5 hover:bg-card-solid', breadcrumb.length <= 1 && 'font-semibold text-fg')}
        >
          {rootName}
        </button>
        {breadcrumb.slice(1).map((crumb, index, all) => (
          <span key={crumb.remoteId} className="flex items-center gap-1">
            <ChevronRight size={11} className="text-fg-subtle" aria-hidden />
            <button
              type="button"
              onClick={() => onNavigate(crumb.remoteId)}
              className={clsx(
                'rounded px-1 py-0.5 hover:bg-card-solid',
                index === all.length - 1 && 'font-semibold text-fg'
              )}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </nav>

      {page.isLoading ? (
        <p className="mt-3 flex items-center gap-2 text-[12.5px] text-fg-subtle">
          <Spinner size={11} /> {fmt.t.common.loading}
        </p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-fg-subtle">{t.browse.nothingHere}</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[34rem] border-collapse text-[13px]">
            <thead className="sr-only">
              <tr>
                <th>{t.selection.columns.included}</th>
                <th>{t.selection.columns.name}</th>
                <th>{t.selection.columns.files}</th>
                <th>{t.selection.columns.size}</th>
              </tr>
            </thead>
            <tbody>
              {folders.map((item) => {
                const { state, rule } = effectiveOf(item)
                const detail = folderRowDetail(fmt, { ...item, effective: state, rule })
                return (
                  <tr key={item.id} className="border-t border-rule first:border-t-0">
                    <td className="w-[36px] px-2 py-2 align-top">
                      {!readOnly && (
                        <TriStateBox
                          state={state}
                          label={t.selection.include(item.name, detail)}
                          onChange={() => onToggle(item, state === 'all' ? 'exclude' : 'include')}
                        />
                      )}
                    </td>
                    <td className="px-1 py-2">
                      <button
                        type="button"
                        onClick={() => onNavigate(item.remoteId)}
                        aria-label={t.browse.open(item.name)}
                        className="flex items-center gap-1.5 text-left font-medium tracking-[-0.014em] underline decoration-transparent underline-offset-2 transition-colors duration-200 hover:decoration-current"
                      >
                        {state === 'none' ? (
                          <Folder size={13} className="shrink-0 text-fg-subtle" aria-hidden />
                        ) : (
                          <FolderOpen size={13} className="shrink-0 text-grounded" aria-hidden />
                        )}
                        <span className="truncate">{item.name}</span>
                      </button>
                      <span
                        className={clsx('readout mt-0.5 block', state === 'none' ? 'text-fg-subtle' : 'text-fg-muted')}
                      >
                        {detail}
                      </span>
                    </td>
                    <td className="readout whitespace-nowrap px-2 py-2 text-right align-top">
                      {t.selection.files(fmt, item.subtree.files)}
                    </td>
                    <td className="readout whitespace-nowrap px-2 py-2 text-right align-top text-fg-subtle">
                      {item.subtree.bytes > 0
                        ? formatBytes(state === 'none' ? item.subtree.bytes : item.subtree.includedBytes)
                        : ''}
                    </td>
                  </tr>
                )
              })}

              {tickable
                ? files.map((item) => {
                    const { state } = effectiveOf(item)
                    const filtered =
                      item.decision === 'excluded' &&
                      item.reason !== 'rule' &&
                      item.reason !== 'not_selected' &&
                      item.reason !== 'removed_by_steward'
                    return (
                      <tr key={item.id} className="border-t border-rule">
                        <td className="w-[36px] px-2 py-1.5 align-top">
                          {!readOnly && (
                            <TriStateBox
                              state={state}
                              disabled={filtered}
                              label={t.selection.include(
                                item.name,
                                filtered
                                  ? t.reason(item.reason, item.detail)
                                  : state === 'all'
                                    ? t.selection.includedMark
                                    : t.selection.leftOutMark
                              )}
                              onChange={() => onToggle(item, state === 'all' ? 'exclude' : 'include')}
                            />
                          )}
                        </td>
                        <td className={clsx('px-1 py-1.5', (filtered || state === 'none') && 'text-fg-subtle')}>
                          <span className="block truncate">{item.name}</span>
                          {(filtered || state === 'none') && (
                            <span className="readout block text-fg-subtle">
                              {filtered ? t.reason(item.reason, item.detail) : t.reason('rule', null)}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5" />
                        <td className="readout whitespace-nowrap px-2 py-1.5 text-right align-top text-fg-subtle">
                          {item.size !== null ? formatBytes(item.size) : '—'}
                        </td>
                      </tr>
                    )
                  })
                : null}
            </tbody>
          </table>

          {!tickable && filesAtLevel > 0 && (
            <p className="border-t border-rule px-2 py-2 text-[12.5px] leading-[1.45] text-fg-muted">
              {t.selection.filesInFolder(fmt, filesAtLevel, files.filter((f) => f.decision === 'included').length)}.{' '}
              {t.selection.tooManyToTick}
            </p>
          )}
          {tickable && files.length === 0 && folders.length > 0 && (
            <p className="border-t border-rule px-2 py-2 text-[12.5px] text-fg-subtle">
              {t.selection.filesInFolder(fmt, 0, 0)}
            </p>
          )}
          {page.data?.nextOffset !== null && page.data?.nextOffset !== undefined && (
            <div className="border-t border-rule px-2 py-2">
              <Button size="sm" variant="ghost" onClick={() => setOffset(page.data?.nextOffset ?? 0)}>
                {t.selection.showMore(fmt, items.length, total)}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** A real checkbox in three states. The dash and the words carry the meaning, never a tint alone. */
export const TriStateBox = ({
  state,
  label,
  onChange,
  disabled
}: {
  state: 'all' | 'none' | 'some'
  label: string
  onChange: () => void
  disabled?: boolean
}) => {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some'
  }, [state])
  return (
    <span className="relative inline-flex h-[18px] w-[18px] items-center justify-center">
      <input
        ref={ref}
        type="checkbox"
        aria-label={label}
        aria-checked={state === 'some' ? 'mixed' : state === 'all'}
        checked={state === 'all'}
        disabled={disabled}
        onChange={onChange}
        className="h-[15px] w-[15px] cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
      />
      {state === 'some' && (
        <Minus size={10} strokeWidth={3} className="pointer-events-none absolute text-primary" aria-hidden />
      )}
    </span>
  )
}
